// CRUD de bodegas. Port de views/bodegas.js.
import { useState } from 'react';
import { useBodegas, useCrearBodega, useEditarBodega, useEliminarBodega } from '../hooks/useBodegas';
import { DataTable, type Column } from '../components/DataTable';
import { EstadoBadge, Badge } from '../components/Badge';
import { Thumb } from '../components/Thumb';
import { Modal } from '../components/Modal';
import { Field } from '../components/Field';
import { SkeletonTable } from '../components/Skeletons';
import { ErrorBox } from '../components/ErrorBox';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/Confirm';
import type { Bodega, Estado } from '../types';

export function Bodegas() {
  const { data: bodegas, isLoading, isError, error } = useBodegas();
  const [modal, setModal] = useState<{ open: boolean; bodega?: Bodega }>({ open: false });
  const eliminar = useEliminarBodega();
  const toast = useToast();
  const confirm = useConfirm();

  const abrirNueva = () => setModal({ open: true, bodega: undefined });

  const borrar = async (b: Bodega) => {
    const ok = await confirm({ title: 'Eliminar bodega', message: `¿Eliminar "${b.nombre}"? Esta acción no se puede deshacer.` });
    if (!ok) return;
    try {
      await eliminar.mutateAsync(b.id);
      toast.success('Bodega eliminada');
    } catch (e) {
      toast.error('No se pudo eliminar', (e as Error).message);
    }
  };

  const cols: Column<Bodega>[] = [
    {
      key: 'nombre', label: 'Bodega',
      render: (r) => (
        <div className="cell-item">
          <Thumb icon="fa-warehouse" />
          <div>
            <b>{r.nombre}</b>{r.principal ? <> <Badge type="primary" dot={false}>Principal</Badge></> : null}
            <br /><small>{r.descripcion || 'Sin descripción'}</small>
          </div>
        </div>
      ),
    },
    { key: 'estado', label: 'Estado', render: (r) => <EstadoBadge estado={r.estado} /> },
    {
      key: '_acc', label: '', sortable: false,
      render: (r) => (
        <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
          <button className="btn ghost sm icon-only" title="Editar" onClick={() => setModal({ open: true, bodega: r })}><i className="fa-solid fa-pen" /></button>
          <button className="btn ghost sm icon-only" title="Eliminar" onClick={() => borrar(r)}><i className="fa-solid fa-trash" /></button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-head">
        <h2>Bodegas</h2>
        <button className="btn primary" onClick={abrirNueva}><i className="fa-solid fa-plus" /> Nueva bodega</button>
      </div>

      {isLoading ? (
        <SkeletonTable rows={5} />
      ) : isError ? (
        <ErrorBox mensaje={(error as Error).message} />
      ) : (
        <DataTable title="Listado de bodegas" pageSize={10} cols={cols} rows={bodegas ?? []} empty="No hay bodegas registradas." />
      )}

      {modal.open ? <BodegaModal bodega={modal.bodega} onClose={() => setModal({ open: false })} /> : null}
    </>
  );
}

function BodegaModal({ bodega, onClose }: { bodega?: Bodega; onClose: () => void }) {
  const editar = !!bodega;
  const crear = useCrearBodega();
  const editarM = useEditarBodega();
  const toast = useToast();
  const [nombre, setNombre] = useState(bodega?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(bodega?.descripcion ?? '');
  const [estado, setEstado] = useState<Estado>(bodega?.estado ?? 'Activo');
  const [principal, setPrincipal] = useState(bodega?.principal ?? false);
  const [enviado, setEnviado] = useState(false);
  const cargando = crear.isPending || editarM.isPending;

  const guardar = async () => {
    setEnviado(true);
    if (!nombre.trim()) return;
    const input = { nombre, descripcion, estado, principal };
    try {
      if (editar) await editarM.mutateAsync({ id: bodega!.id, input });
      else await crear.mutateAsync(input);
      toast.success(editar ? 'Bodega actualizada' : 'Bodega creada', nombre);
      onClose();
    } catch (e) {
      toast.error('No se pudo guardar', (e as Error).message);
    }
  };

  return (
    <Modal
      open
      title={editar ? 'Editar bodega' : 'Nueva bodega'}
      icon="fa-warehouse"
      iconColor="primary"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className={`btn primary${cargando ? ' loading' : ''}`} onClick={guardar}>{editar ? 'Guardar cambios' : 'Crear bodega'}</button>
        </>
      }
    >
      <Field name="nombre" label="Nombre" required value={nombre} onChange={setNombre} placeholder="Ej: Bodega Norte" invalid={enviado && !nombre.trim()} />
      <Field name="descripcion" label="Descripción" type="textarea" value={descripcion} onChange={setDescripcion} placeholder="Ubicación o notas" />
      <Field name="estado" label="Estado" type="select" value={estado} onChange={(v) => setEstado(v as Estado)}
        options={[{ value: 'Activo', label: 'Activo' }, { value: 'Inactivo', label: 'Inactivo' }]} />
      <label className="check-row">
        <input type="checkbox" checked={principal} onChange={(e) => setPrincipal(e.target.checked)} />
        <span>Bodega principal<br /><small className="text-muted">Se usa por defecto al fabricar, deshacer y mover stock. Solo una puede serlo.</small></span>
      </label>
    </Modal>
  );
}
